import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { PageHeader, StatCard, Section, Spinner, RiskBadge } from "@/components/ui-parts";
import {
  RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { Shield, Activity, Target, ClipboardCheck, TrendingUp } from "lucide-react";

export default function ExecutiveDashboard() {
  const { withScope, activeTenantId } = useTenant();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [framework, setFramework] = useState("");
  const [frameworks, setFrameworks] = useState([]);
  const [org, setOrg] = useState({});
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    api.get("/reference/frameworks").then(({ data }) => setFrameworks(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = withScope();
    if (framework) params.framework = framework;
    Promise.all([
      api.get("/dashboard/stats", { params }),
      api.get("/settings/organization", { params: withScope() }),
    ])
      .then(([a, b]) => { setStats(a.data); setOrg(b.data || {}); })
      .catch(() => { setStats(null); setOrg({}); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [activeTenantId, framework]);

  // Load trend for primary framework (or currently-viewed framework)
  useEffect(() => {
    const fw = framework || org.primaryFramework;
    if (!fw) { setTrend([]); return; }
    api.get(`/snapshots/framework/${fw}`, { params: withScope() })
      .then(({ data }) => setTrend(data))
      .catch(() => setTrend([]));
    // eslint-disable-next-line
  }, [activeTenantId, framework, org.primaryFramework]);

  if (loading || !stats) {
    return (
      <div className="p-8">
        <PageHeader eyebrow="Overview" title="Executive Dashboard" />
        <Spinner label="Loading assessment metrics..." />
      </div>
    );
  }

  const gaugeData = [{ name: "ECRI", value: stats.ecri, fill: stats.ecri >= 80 ? "#10B981" : stats.ecri >= 65 ? "#00E5FF" : stats.ecri >= 50 ? "#F59E0B" : "#EF4444" }];

  return (
    <div className="p-8 max-w-[1600px]" data-testid="executive-dashboard">
      <PageHeader
        eyebrow="Overview"
        title="Executive Dashboard"
        description={framework
          ? `Cyber resilience posture filtered to ${stats.framework_name} requirements only.`
          : "Real-time board-level cyber resilience posture across all governance and capability domains."}
      />

      {/* Framework View toggle */}
      <div className="card p-4 mb-6" data-testid="dashboard-framework-selector">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 mr-2">View:</div>
          <button
            onClick={() => setFramework("")}
            className={`btn btn-sm ${framework === "" ? "btn-primary" : "btn-secondary"}`}
            data-testid="dash-fw-all"
          >
            All Requirements
          </button>
          {frameworks.map((f) => (
            <button
              key={f.id}
              onClick={() => setFramework(f.id)}
              className={`btn btn-sm ${framework === f.id ? "btn-primary" : "btn-secondary"}`}
              data-testid={`dash-fw-${f.id}`}
            >
              <ClipboardCheck size={11} /> {f.name}
            </button>
          ))}
        </div>
      </div>

      {/* Framework Summary Strip (only show in All view) */}
      {!framework && stats.framework_summary?.length > 0 && (
        <Section title="Framework Compliance Overview" description="Maturity avg + control distribution across the tenant's activated audit frameworks. Click a tile to filter the dashboard.">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 p-5" data-testid="framework-summary-strip">
            {stats.framework_summary.map((f) => {
              const total = (f.compliant_count || 0) + (f.partial_count || 0) + (f.noncompliant_count || 0) + (f.not_assessed_count || 0);
              const seg = (n) => total ? (n / total) * 100 : 0;
              const scoreColor = f.avg_score >= 3.5 ? "text-emerald-400" : f.avg_score >= 2.5 ? "text-amber-400" : f.avg_score >= 1.5 ? "text-orange-400" : "text-red-400";
              return (
                <button
                  key={f.framework}
                  onClick={() => setFramework(f.framework)}
                  className="card p-3 text-left hover:border-[#00E5FF]/40 transition-colors"
                  data-testid={`fw-tile-${f.framework}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 truncate">
                      {f.framework_name}
                      {f.framework === org.primaryFramework && <span className="ml-1 text-[#F59E0B]">★</span>}
                    </div>
                    <div className="text-[10px] font-mono text-slate-500">{total} ctrls</div>
                  </div>

                  <div className="flex items-baseline gap-2 mb-2">
                    <div className={`font-mono text-2xl font-semibold ${scoreColor}`}>{f.avg_score.toFixed(2)}</div>
                    <div className="text-[10px] font-mono text-slate-500">avg / 5</div>
                    <div className="ml-auto text-[10px] font-mono text-slate-400">
                      <span className="text-emerald-400">{f.compliance_pct}%</span> compliant
                    </div>
                  </div>

                  {/* Stacked distribution bar */}
                  <div className="w-full h-2 bg-slate-800 rounded-sm overflow-hidden flex">
                    <div className="bg-emerald-500" style={{ width: `${seg(f.compliant_count)}%` }} title={`Compliant: ${f.compliant_count}`} />
                    <div className="bg-amber-500" style={{ width: `${seg(f.partial_count)}%` }} title={`Partial: ${f.partial_count}`} />
                    <div className="bg-red-500" style={{ width: `${seg(f.noncompliant_count)}%` }} title={`Non-compliant: ${f.noncompliant_count}`} />
                    <div className="bg-slate-600" style={{ width: `${seg(f.not_assessed_count)}%` }} title={`Not Assessed: ${f.not_assessed_count}`} />
                  </div>

                  {/* Legend counts */}
                  <div className="flex items-center justify-between text-[9px] font-mono text-slate-500 mt-1.5">
                    <span className="text-emerald-400">C {f.compliant_count}</span>
                    <span className="text-amber-400">P {f.partial_count}</span>
                    <span className="text-red-400">NC {f.noncompliant_count}</span>
                    <span>NA {f.not_assessed_count}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </Section>
      )}

      {/* Compliance Trend for Primary Framework */}
      {trend.length > 0 ? (
        <Section
          title={`Compliance Trend · ${frameworks.find((f) => f.id === (framework || org.primaryFramework))?.name || "Primary Framework"}`}
          description={`${trend.length} snapshot${trend.length === 1 ? "" : "s"} recorded. ${framework ? "Scoped to current view." : "Scoped to tenant's primary framework (★)."} Auto-snapshots run weekly.`}
        >
          <div className="p-5" style={{ height: 240, minHeight: 240 }} data-testid="dashboard-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend.map((s) => ({ date: s.timestamp.slice(0, 10), compliance: s.compliance_pct, avg: s.overall_avg_score, source: s.source || "manual" }))}
                         margin={{ top: 10, right: 24, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} label={{ value: "%", angle: -90, position: "insideLeft", style: { fill: "#64748B", fontSize: 10 } }} />
                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", fontSize: 12 }} />
                <Line type="monotone" dataKey="compliance" stroke="#00E5FF" strokeWidth={2} dot={{ fill: "#00E5FF", r: 4 }} name="Compliance %" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>
      ) : (org.primaryFramework || framework) && (
        <Section title="Compliance Trend" description="Auto-snapshots build history over time. Save one from Framework Report to bootstrap the chart.">
          <div className="p-8 text-center" data-testid="dashboard-trend-empty">
            <TrendingUp size={24} className="mx-auto text-slate-600 mb-3" strokeWidth={1.5} />
            <div className="text-xs text-slate-500">
              No snapshots yet for {frameworks.find((f) => f.id === (framework || org.primaryFramework))?.name || "the primary framework"}.
            </div>
          </div>
        </Section>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card card-accent p-5" data-testid="ecri-card">
          <div className="stat-label">{framework ? `${stats.framework_name} ECRI` : "Executive Cyber Resilience Index"}</div>
          <div className="flex items-center gap-3">
            <div className="w-24 h-24" style={{ minWidth: 96, minHeight: 96 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart innerRadius="65%" outerRadius="100%" data={gaugeData} startAngle={90} endAngle={-270}>
                  <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                  <RadialBar background={{ fill: "#1E293B" }} dataKey="value" cornerRadius={2} />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <div className="stat-value" data-testid="ecri-value">{stats.ecri}</div>
              <div className="text-[10px] font-mono text-slate-500 uppercase">/ 100</div>
              <div className="text-xs text-slate-300 mt-1">{stats.certification}</div>
            </div>
          </div>
        </div>

        <StatCard testid="assessed-card" label="Assessed Requirements" value={`${stats.assessed}/${stats.total}`} subValue={`${Math.round((stats.assessed / (stats.total || 1)) * 100)}% coverage`} />
        <StatCard testid="maturity-card" label="Average Maturity" value={stats.avg_score.toFixed(2)} subValue={stats.maturity} />
        <StatCard testid="highrisk-card" tone={stats.high_risk_count > 20 ? "critical" : stats.high_risk_count > 0 ? "warning" : "default"}
          label="High / Critical Risks" value={stats.high_risk_count} subValue={`${stats.critical_count} critical`} />
      </div>

      {/* Domain Heatmap */}
      <Section title="Domain Heat Map" description={framework ? `${stats.framework_name}-mapped domain capability maturity` : "Capability maturity across all GACRF domains"}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-5">
          {stats.domains.map((d) => {
            const cls = Math.max(0, Math.min(5, Math.round(d.score)));
            return (
              <div key={d.domain} className={`heat-tile heat-${cls}`} data-testid={`heat-${d.domain}`}>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-300 truncate">{d.domain}</div>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-mono text-2xl font-semibold text-slate-100">{d.score.toFixed(2)}</div>
                  <div className="text-[10px] font-mono text-slate-400">/5</div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-mono text-slate-400">{d.maturity}</div>
                  <div className="text-[10px] font-mono text-slate-500">{d.assessed}/{d.total}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Top Gaps */}
      <Section title="Top Improvement Priorities" description={framework ? `High-risk ${stats.framework_name} requirements with lowest maturity` : "High-risk requirements with lowest maturity scores"}>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="top-gaps-table">
            <thead>
              <tr>
                <th>Req ID</th><th>Domain</th><th>Capability</th>
                <th>Risk</th><th>Score</th><th>Gap</th>
              </tr>
            </thead>
            <tbody>
              {stats.top_gaps.map((r) => (
                <tr key={r.requirementId}>
                  <td className="mono">{r.requirementId}</td>
                  <td>{r.domain}</td>
                  <td>{r.capability}</td>
                  <td><RiskBadge level={r.riskLevel} /></td>
                  <td className="mono">{r.score.toFixed(1)}</td>
                  <td className="text-slate-300 max-w-md">{r.gap}</td>
                </tr>
              ))}
              {stats.top_gaps.length === 0 && (
                <tr><td colSpan={6} className="text-center text-slate-500 py-6">No high-risk gaps identified.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-5">
          <Shield size={20} className="text-[#00E5FF] mb-3" strokeWidth={1.5} />
          <div className="stat-label">Certification Track</div>
          <div className="stat-value">{stats.certification}</div>
          <div className="text-xs text-slate-400 mt-2">
            {stats.certification === "Not Ready" ? "Focus on foundational governance." : `Target next level to elevate ECRI above ${stats.ecri >= 90 ? 100 : stats.ecri >= 80 ? 90 : stats.ecri >= 65 ? 80 : stats.ecri >= 50 ? 65 : 50}.`}
          </div>
        </div>
        <div className="card p-5">
          <Activity size={20} className="text-[#F59E0B] mb-3" strokeWidth={1.5} />
          <div className="stat-label">Risk Posture</div>
          <div className="stat-value">{stats.risk_posture}</div>
          <div className="text-xs text-slate-400 mt-2">Overall enterprise cyber risk exposure across capabilities.</div>
        </div>
        <div className="card p-5">
          <Target size={20} className="text-[#10B981] mb-3" strokeWidth={1.5} />
          <div className="stat-label">Coverage</div>
          <div className="stat-value">{Math.round((stats.assessed / (stats.total || 1)) * 100)}%</div>
          <div className="text-xs text-slate-400 mt-2">{stats.assessed} of {stats.total} requirements scored.</div>
        </div>
      </div>
    </div>
  );
}
